// src/components/Merchants/Modal/AddEquipmentModal.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';

const defaultEquipment = {
  equipment_type_id: '',
  equipment_type_custom: '',
  equipment_model_id: '',
  equipment_model_custom: '',
  manufacturer_id: '',
  manufacturer_custom: '',
  status_id: '',
  sku_number: '',
  add_terminal: '',
  add_gateway: '',
  note: '',
};

const AddEquipmentModal = ({
  open,
  onClose,
  equipment = defaultEquipment,
  onEquipmentChange,
  typeOptions = [],
  manufacturerOptions = [],
  modelOptions = [],
  statusOptions = [],
  processing = false,
  title = 'Add New Equipment',
  onConfirm,
}) => {
  const handleFieldChange = (field, value) => {
    const next = { ...equipment, [field]: value };
    if (field === 'equipment_type_id' || field === 'manufacturer_id' || field === 'equipment_type_custom' || field === 'manufacturer_custom') {
      next.equipment_model_id = '';
      next.equipment_model_custom = '';
    }
    onEquipmentChange(next);
  };

  const opts = (arr) => (Array.isArray(arr) ? arr : []);
  const typeOpts = opts(typeOptions).map((o) => ({ id: o.id ?? o.value, name: o.name ?? o.label ?? String(o.id ?? o.value) }));
  const manufacturerOpts = opts(manufacturerOptions).map((o) => ({ id: o.id ?? o.value, name: o.name ?? o.label ?? String(o.id ?? o.value) }));
  const modelOpts = opts(modelOptions).map((o) => ({ id: o.id ?? o.value, name: o.name ?? o.label ?? String(o.id ?? o.value) }));
  const statusOpts = opts(statusOptions).map((o) => ({ id: o.id ?? o.value, name: o.name ?? o.label ?? String(o.id ?? o.value) }));

  const hasType = equipment.equipment_type_id || (equipment.equipment_type_custom && String(equipment.equipment_type_custom).trim());
  const hasManufacturer = equipment.manufacturer_id || (equipment.manufacturer_custom && String(equipment.manufacturer_custom).trim());
  const hasModel = equipment.equipment_model_id || (equipment.equipment_model_custom && String(equipment.equipment_model_custom).trim());
  const canSelectModel = hasType && hasManufacturer;

  const typeDisplayValue = equipment.equipment_type_id
    ? (typeOpts.find((o) => o.id === equipment.equipment_type_id) || null)
    : (equipment.equipment_type_custom ? equipment.equipment_type_custom : null);
  const manufacturerDisplayValue = equipment.manufacturer_id
    ? (manufacturerOpts.find((o) => o.id === equipment.manufacturer_id) || null)
    : (equipment.manufacturer_custom ? equipment.manufacturer_custom : null);
  const modelDisplayValue = equipment.equipment_model_id
    ? (modelOpts.find((o) => o.id === equipment.equipment_model_id) || null)
    : (equipment.equipment_model_custom ? equipment.equipment_model_custom : null);

  const typeInputValue = equipment.equipment_type_id
    ? (typeOpts.find((o) => o.id === equipment.equipment_type_id)?.name ?? '')
    : (equipment.equipment_type_custom ?? '');
  const manufacturerInputValue = equipment.manufacturer_id
    ? (manufacturerOpts.find((o) => o.id === equipment.manufacturer_id)?.name ?? '')
    : (equipment.manufacturer_custom ?? '');
  const modelInputValue = equipment.equipment_model_id
    ? (modelOpts.find((o) => o.id === equipment.equipment_model_id)?.name ?? '')
    : (equipment.equipment_model_custom ?? '');

  const handleTypeInputChange = (e, value) => {
    if (equipment.equipment_type_id) return;
    onEquipmentChange({ ...equipment, equipment_type_custom: value ?? '', equipment_model_id: '', equipment_model_custom: '' });
  };
  const handleManufacturerInputChange = (e, value) => {
    if (equipment.manufacturer_id) return;
    onEquipmentChange({ ...equipment, manufacturer_custom: value ?? '', equipment_model_id: '', equipment_model_custom: '' });
  };
  const handleModelInputChange = (e, value) => {
    if (equipment.equipment_model_id) return;
    onEquipmentChange({ ...equipment, equipment_model_custom: value ?? '' });
  };

  const handleTypeChange = (_, newValue) => {
    const next = { ...equipment, equipment_model_id: '', equipment_model_custom: '' };
    if (typeof newValue === 'string') {
      next.equipment_type_id = '';
      next.equipment_type_custom = newValue;
    } else if (newValue && newValue.id) {
      next.equipment_type_id = newValue.id;
      next.equipment_type_custom = '';
    } else {
      next.equipment_type_id = '';
      next.equipment_type_custom = '';
    }
    onEquipmentChange(next);
  };
  const handleManufacturerChange = (_, newValue) => {
    const next = { ...equipment, equipment_model_id: '', equipment_model_custom: '' };
    if (typeof newValue === 'string') {
      next.manufacturer_id = '';
      next.manufacturer_custom = newValue;
    } else if (newValue && newValue.id) {
      next.manufacturer_id = newValue.id;
      next.manufacturer_custom = '';
    } else {
      next.manufacturer_id = '';
      next.manufacturer_custom = '';
    }
    onEquipmentChange(next);
  };
  const handleModelChange = (_, newValue) => {
    const next = { ...equipment };
    if (typeof newValue === 'string') {
      next.equipment_model_id = '';
      next.equipment_model_custom = newValue;
    } else if (newValue && newValue.id) {
      next.equipment_model_id = newValue.id;
      next.equipment_model_custom = '';
    } else {
      next.equipment_model_id = '';
      next.equipment_model_custom = '';
    }
    onEquipmentChange(next);
  };

  const isValid =
    hasType &&
    hasModel &&
    hasManufacturer &&
    equipment.status_id &&
    (equipment.sku_number !== undefined && equipment.sku_number !== null && String(equipment.sku_number).trim() !== '');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          p: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#154A8A',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={typeOpts}
              value={typeDisplayValue}
              inputValue={typeInputValue}
              onInputChange={handleTypeInputChange}
              onChange={handleTypeChange}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.name ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Equipment Type"
                  variant="outlined"
                  required
                  placeholder="Select or type new..."
                  disabled={processing}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={manufacturerOpts}
              value={manufacturerDisplayValue}
              inputValue={manufacturerInputValue}
              onInputChange={handleManufacturerInputChange}
              onChange={handleManufacturerChange}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.name ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manufacturer"
                  variant="outlined"
                  required
                  placeholder="Select or type new..."
                  disabled={processing}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={modelOpts}
              value={modelDisplayValue}
              inputValue={modelInputValue}
              onInputChange={handleModelInputChange}
              onChange={handleModelChange}
              disabled={!canSelectModel}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.name ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Equipment Model"
                  variant="outlined"
                  required
                  placeholder={canSelectModel ? 'Select or type new...' : 'Select type and manufacturer first'}
                  disabled={processing || !canSelectModel}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="SKU Number"
              value={equipment.sku_number ?? ''}
              onChange={(e) => handleFieldChange('sku_number', e.target.value)}
              variant="outlined"
              fullWidth
              disabled={processing}
              placeholder="e.g. 12345"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" required>
              <InputLabel>Machine Status</InputLabel>
              <Select
                value={equipment.status_id ?? ''}
                onChange={(e) => handleFieldChange('status_id', e.target.value)}
                label="Machine Status"
                disabled={processing}
              >
                <MenuItem value="">Select status</MenuItem>
                {statusOpts.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid item xs={12}>
            <TextField
              label="Add Terminal (optional)"
              value={equipment.add_terminal ?? ''}
              onChange={(e) => handleFieldChange('add_terminal', e.target.value)}
              variant="outlined"
              fullWidth
              disabled={processing}
              placeholder="Terminal"
            />
          </Grid> */}

          {/* <Grid item xs={12}>
            <TextField
              label="Add Gateway (optional)"
              value={equipment.add_gateway ?? ''}
              onChange={(e) => handleFieldChange('add_gateway', e.target.value)}
              variant="outlined"
              fullWidth
              disabled={processing}
              placeholder="Gateway"
            />
          </Grid> */}

          <Grid item xs={12}>
            <TextField
              label="Note (optional)"
              value={equipment.note ?? ''}
              onChange={(e) => handleFieldChange('note', e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              disabled={processing}
              placeholder="e.g. Testing note"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            You can select from the list or type a new Equipment Type, Manufacturer, or Model to create it on submit.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!isValid || processing}
          startIcon={processing ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            bgcolor: '#154A8A',
            '&:hover': { bgcolor: '#0d3a6a' },
          }}
        >
          Add Equipment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEquipmentModal;
